import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Star, CalendarCheck } from "lucide-react";
import { StarMark, Wordmark } from "./StarMark";
import { SERVICES, waLink, DEFAULT_WA_TEXT } from "../lib/data";

const WaIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
  </svg>
);

export const WaIconExport = WaIcon;

const navLinkClass = ({ isActive }) =>
  `relative text-sm font-bold tracking-wide transition-colors duration-300 ${
    isActive ? "text-brand" : "text-cocoa hover:text-brand"
  }`;

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`glass fixed inset-x-0 top-0 z-50 border-b-2 border-gold/50 transition-shadow duration-500 ${
          scrolled ? "shadow-lg shadow-brand/20" : ""
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
          <Link
            to="/"
            data-testid="nav-link-home-logo"
            className="flex items-center gap-3 transition-opacity hover:opacity-85"
          >
            <StarMark size={38} />
            <Wordmark onLight className="text-lg sm:text-xl" />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            <NavLink to="/" data-testid="nav-link-home" end className={navLinkClass}>
              Home
            </NavLink>
            <NavLink to="/aanbod" data-testid="nav-link-aanbod" className={navLinkClass}>
              Aanbod
            </NavLink>

            <div className="group relative">
              <button
                data-testid="nav-dropdown-diensten"
                className="flex items-center gap-1.5 text-sm font-bold tracking-wide text-cocoa transition-colors duration-300 group-hover:text-brand"
              >
                Diensten
                <Star size={11} className="fill-gold text-gold transition-transform duration-500 group-hover:rotate-180" />
              </button>
              <div className="pointer-events-none absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 pt-4 opacity-0 transition-all duration-300 group-hover:pointer-events-auto group-hover:opacity-100">
                <div className="rounded-2xl border-2 border-gold/40 bg-white p-2 shadow-2xl shadow-brand/25">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/diensten/${s.slug}`}
                      data-testid={`nav-dropdown-${s.slug}`}
                      className="flex items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold text-cocoa transition-colors hover:bg-gold/15 hover:text-brand"
                    >
                      {s.title}
                      <Star size={10} className="fill-gold text-gold" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <NavLink to="/contact" data-testid="nav-link-contact" className={navLinkClass}>
              Contact
            </NavLink>
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a
              href={waLink(DEFAULT_WA_TEXT)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-button"
              aria-label="WhatsApp"
              className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-whatsapp/60 text-whatsapp transition-all duration-300 hover:bg-whatsapp hover:text-white"
            >
              <WaIcon />
            </a>
            <Link
              to="/contact"
              data-testid="nav-cta-book"
              className="joy-hover relative overflow-hidden rounded-full bg-gold px-6 py-2.5 text-sm font-extrabold text-brand-dark"
            >
              <span className="relative z-10 flex items-center gap-2">
                Boek nu!
                <CalendarCheck size={15} />
              </span>
              <span className="absolute inset-0 -translate-x-full bg-gold-light transition-transform duration-500 hover:translate-x-0" />
            </Link>
          </div>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-brand/40 text-brand lg:hidden"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col bg-cream/98 pt-28 backdrop-blur-xl lg:hidden"
          >
            <nav className="flex flex-col gap-1 px-8">
              {[
                { to: "/", label: "Home", id: "mobile-link-home" },
                { to: "/aanbod", label: "Aanbod", id: "mobile-link-aanbod" },
                ...SERVICES.map((s) => ({
                  to: `/diensten/${s.slug}`,
                  label: s.title,
                  id: `mobile-link-${s.slug}`,
                })),
                { to: "/contact", label: "Contact & Boeken", id: "mobile-link-contact" },
              ].map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.4 }}
                >
                  <Link
                    to={l.to}
                    data-testid={l.id}
                    className="block border-b border-gold/40 py-4 font-display text-2xl font-extrabold text-cocoa transition-colors hover:text-brand"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-auto flex gap-3 p-8"
            >
              <Link
                to="/contact"
                data-testid="mobile-cta-book"
                className="flex-1 rounded-full bg-gold py-4 text-center font-extrabold text-brand-dark"
              >
                Boek nu!
              </Link>
              <a
                href={waLink(DEFAULT_WA_TEXT)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp-button"
                className="flex h-14 w-14 items-center justify-center rounded-full bg-whatsapp text-white"
                aria-label="WhatsApp"
              >
                <WaIcon size={22} />
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
